// Listing lifecycle on the client: one vocabulary for statuses, one place that
// changes them (with cache invalidation, renter alerts on republish and the
// renter-notification drain), completeness checks shared by the dashboard and
// the edit page, and the owner-facing delete that cleans storage after the row
// is gone. The DB does the rest: pausing / renting / archiving / deleting a
// listing or changing its availability closes pending requests that no longer
// fit (see supabase/migrations/20260913120000_listing_lifecycle.sql).
import { supabase } from "@/integrations/supabase/client";
import { queryClient } from "@/lib/queryClient";
import { containsContactInfo } from "@/lib/contactInfoValidation";
import { track } from "@/lib/track";

export const PROPERTY_STATUSES = ["available", "unlisted", "rented", "draft", "archived", "blocked"] as const;
export type PropertyStatus = (typeof PROPERTY_STATUSES)[number];

export const PUBLISHED_STATUS: PropertyStatus = "available";
export const PAUSED_STATUS: PropertyStatus = "unlisted";
export const RENTED_STATUS: PropertyStatus = "rented";
/** Statuses only the team sets; the DB stops owners from leaving them. */
export const TEAM_ONLY_STATUSES: readonly PropertyStatus[] = ["archived", "blocked"];

export const isPublished = (status: string | null | undefined): boolean => status === PUBLISHED_STATUS;
export const isTeamLocked = (status: string | null | undefined): boolean =>
  TEAM_ONLY_STATUSES.includes((status ?? "") as PropertyStatus);
export const isPropertyStatus = (s: string): s is PropertyStatus =>
  (PROPERTY_STATUSES as readonly string[]).includes(s);

export type StatusTone = "neutral" | "info" | "success" | "warn" | "danger" | "muted";
export interface StatusPresentation {
  label: string;
  tone: StatusTone;
  /** One line for tooltips and the edit-page header. */
  description: string;
}

export const STATUS_LABELS: Record<PropertyStatus, string> = {
  available: "Published",
  unlisted: "Paused",
  rented: "Rented",
  draft: "Draft",
  archived: "Removed by the team",
  blocked: "Blocked",
};

export const statusPresentation = (status: string | null | undefined): StatusPresentation => {
  switch (status) {
    case "available":
      return { label: "Published", tone: "success", description: "Visible to renters." };
    case "unlisted":
      return { label: "Paused", tone: "warn", description: "Hidden from renters. Everything is saved for when you republish." };
    case "rented":
      return { label: "Rented", tone: "info", description: "Marked as rented and hidden from renters. Set a date to relist it." };
    case "draft":
      return { label: "Draft", tone: "muted", description: "Not published yet." };
    case "archived":
      return { label: "Removed by the team", tone: "danger", description: "Contact the team to bring this listing back." };
    case "blocked":
      return { label: "Blocked", tone: "danger", description: "Contact the team about this listing." };
    default:
      return { label: "Unpublished", tone: "muted", description: "Hidden from renters." };
  }
};

// ---- Completeness (shared by the dashboard publish action and the edit page)

export const MIN_PRICE = 500;

export interface ListingCompletenessSource {
  title?: string | null;
  location?: string | null;
  type?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  price?: number | null;
  min_rental_months?: number | null;
  available_date?: string | null;
  available_until?: string | null;
  amenities?: string[] | null;
  description?: string | null;
}

/**
 * What still blocks publishing, as owner-facing sentences. Empty = complete.
 * Only what a listing can't exist without: amenities, photos and the
 * description are suggestions, and each market's quality rules in the back
 * office decide what renters see (Felipe, Oct 18).
 */
export const listingCompletenessIssues = (row: ListingCompletenessSource, symbol = "$"): string[] => {
  const issues: string[] = [];
  if (!row.title?.trim()) issues.push("Add a title");
  if (!row.location?.trim()) issues.push("Choose the area");
  if (!row.type?.trim()) issues.push("Choose the type of home");
  if (row.bedrooms == null || row.bathrooms == null) issues.push("Set bedrooms and bathrooms");
  if (!(Number(row.price) >= MIN_PRICE)) issues.push(`Set a monthly price of at least ${symbol}${MIN_PRICE}`);
  if (!(Number(row.min_rental_months) >= 1)) issues.push("Set a minimum stay of at least 1 month");
  if (row.available_date && row.available_until && row.available_date > row.available_until) {
    issues.push("The availability window ends before it starts");
  }
  if (row.description && containsContactInfo(row.description)) issues.push("Remove contact details from the description");
  return issues;
};

export const isListingComplete = (row: ListingCompletenessSource): boolean => listingCompletenessIssues(row).length === 0;

// ---- Status changes

const warn = (what: string) => (e: unknown) => console.warn(`${what} failed`, e);

/** Listing pages and grids re-read after any status or availability change. */
export const invalidateListingQueries = (): void => {
  for (const key of ["property", "properties", "most-viewed-properties"]) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
};

/** Emails renters whose pending requests were just closed by the DB trigger. */
export const drainRequestNotifications = (): void => {
  supabase.functions.invoke("process-contact-request-notifications").catch(warn("process-contact-request-notifications"));
};

const afterListingChange = (id: string, republished: boolean): void => {
  invalidateListingQueries();
  if (republished) {
    supabase.functions.invoke("notify-matching-renters", { body: { propertyId: id } }).catch(warn("notify-matching-renters"));
  }
  drainRequestNotifications();
};

export interface StatusChangeResult {
  error: Error | null;
  /** The row after the write (quality checks run in the DB on publish). */
  row: { status: string; quality_visible: boolean } | null;
}

export type AvailabilityPatch = {
  available_date?: string | null;
  available_until?: string | null;
  unavailable_dates?: string[];
  min_rental_months?: number;
};

export const setListingStatus = async (
  id: string,
  status: PropertyStatus,
  extra: AvailabilityPatch & { relist_reminder_at?: string | null } = {},
): Promise<StatusChangeResult> => {
  const { error } = await supabase
    .from("properties")
    .update({ status, ...extra } as never)
    .eq("id", id);
  if (error) return { error: new Error(error.message), row: null };
  // Read back after the write: the quality gate runs in an AFTER trigger, so a
  // RETURNING clause would still show the pre-trigger flag.
  const { data } = await supabase.from("properties").select("id, status, quality_visible").eq("id", id).maybeSingle();
  afterListingChange(id, status === PUBLISHED_STATUS);
  return {
    error: null,
    row: data ? { status: data.status, quality_visible: data.quality_visible ?? true } : { status, quality_visible: true },
  };
};

export const pauseListing = async (id: string, reminderDate?: string | null): Promise<StatusChangeResult> => {
  const res = await setListingStatus(id, PAUSED_STATUS, { relist_reminder_at: reminderDate ?? null });
  if (!res.error) track("listing_paused", { property_id: id, has_reminder: !!reminderDate });
  return res;
};

export const markRented = async (id: string): Promise<StatusChangeResult> => {
  const res = await setListingStatus(id, RENTED_STATUS);
  if (!res.error) track("listing_rented", { property_id: id });
  return res;
};

export const republishListing = async (id: string): Promise<StatusChangeResult> => {
  const res = await setListingStatus(id, PUBLISHED_STATUS);
  if (!res.error) track("listing_republished", { property_id: id });
  return res;
};

export const updateListingAvailability = async (id: string, patch: AvailabilityPatch): Promise<{ error: Error | null }> => {
  const { error } = await supabase.from("properties").update(patch as never).eq("id", id);
  if (error) return { error: new Error(error.message) };
  afterListingChange(id, false);
  track("availability_updated", { property_id: id, fields: Object.keys(patch) });
  return { error: null };
};

/**
 * "I found a tenant, it's free again on X": keep (or put) the listing live
 * with the new window start so renters searching later months still find it.
 */
export const keepPublishedFrom = async (
  id: string,
  availableDate: string,
  currentStatus: string | null | undefined,
): Promise<{ error: Error | null }> => {
  if (isPublished(currentStatus)) return updateListingAvailability(id, { available_date: availableDate });
  const res = await setListingStatus(id, PUBLISHED_STATUS, { available_date: availableDate });
  return { error: res.error };
};

// ---- Delete (row first, storage after, a failed delete must not leave a
// listing with broken photos)

/** Object key inside `bucket` for a public or render URL, or null. */
export const storagePathFromUrl = (url: string | null | undefined, bucket: string): string | null => {
  if (!url) return null;
  const marker = `/${bucket}/`;
  const idx = url.indexOf(marker);
  if (idx < 0) return null;
  const key = url.slice(idx + marker.length).split("?")[0];
  try {
    return decodeURIComponent(key) || null;
  } catch {
    return key || null;
  }
};

export interface DeletableListing {
  id: string;
  photos?: unknown;
  image_urls?: string[] | null;
  video_urls?: string[] | null;
}

const uniq = (xs: (string | null)[]): string[] => [...new Set(xs.filter((x): x is string => !!x))];

export const listingStoragePaths = (listing: DeletableListing) => {
  const photos = Array.isArray(listing.photos)
    ? (listing.photos as { url?: string; originalPath?: string }[])
    : [];
  return {
    images: uniq([...photos.map((p) => p.url ?? null), ...(listing.image_urls ?? [])].map((u) => storagePathFromUrl(u, "property-images"))),
    originals: uniq(photos.map((p) => p.originalPath ?? null)),
    videos: uniq((listing.video_urls ?? []).map((u) => storagePathFromUrl(u, "property-videos"))),
  };
};

// An owner's delete keeps the home, archived and marked deleted, with its
// photos (Felipe, Oct 27): the team sees it in Admin, can read why it left,
// and can restore it. Pending requests close as before (owner_delete_listing).
export const deleteListing = async (listing: DeletableListing): Promise<{ error: Error | null }> => {
  const { error } = await (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => PromiseLike<{ error: { message: string } | null }>)(
    "owner_delete_listing",
    { p_property: listing.id },
  );
  if (error) return { error: new Error(error.message) };
  invalidateListingQueries();
  drainRequestNotifications();
  track("listing_deleted", { property_id: listing.id });
  return { error: null };
};

// ---- Why owners delist (listing_delist_events; not in the generated types)

interface UntypedDb {
  from(table: string): {
    insert(row: Record<string, unknown>): {
      select(cols: string): { single(): PromiseLike<{ data: { id: string } | null; error: { message: string } | null }> };
    };
    update(patch: Record<string, unknown>): {
      eq(col: string, v: string): PromiseLike<{ error: { message: string } | null }>;
    };
  };
}
const db = supabase as unknown as UntypedDb;

export interface DelistEventInput {
  property_id: string;
  property_title: string | null;
  owner_id: string;
  tenant_id: string | null;
  reason_code: string;
  reason_text?: string | null;
  found_via?: string | null;
  matched_request_id?: string | null;
}

export interface DelistEventOutcome {
  action: "undecided" | "kept_published" | "paused" | "rented" | "deleted";
  recovered?: boolean;
  step_reached?: number;
  available_again?: string | null;
  relist_reminder_at?: string | null;
}

/** Inserted the moment a reason is picked; returns the row id (null on failure). */
export const recordDelistEvent = async (input: DelistEventInput): Promise<string | null> => {
  const { data, error } = await db.from("listing_delist_events").insert({ ...input }).select("id").single();
  if (error) {
    console.warn("recordDelistEvent failed", error.message);
    return null;
  }
  return data?.id ?? null;
};

export const updateDelistEvent = async (id: string | null, outcome: DelistEventOutcome): Promise<void> => {
  if (!id) return;
  const { error } = await db.from("listing_delist_events").update({ ...outcome }).eq("id", id);
  if (error) console.warn("updateDelistEvent failed", error.message);
};
